import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Copy, User, Calendar as CalendarIcon } from "@phosphor-icons/react";

/**
 * Duplicate an existing intervention onto another day / another technician.
 * The backend clones only the "template" fields (chantier, title, time, duration…)
 * — runtime state (actual hours, timer, debrief, GPS) is never copied.
 *
 * Props:
 *   - intervention: the source intervention doc (needs id, scheduled_date, technician_id, title)
 *   - team: array of active users to pick from (id, name, color, role)
 *   - onDuplicated(clone): called after a successful clone
 */
export default function DuplicateInterventionDialog({ open, onOpenChange, intervention, team = [], onDuplicated }) {
  const [day, setDay] = useState("");
  const [techId, setTechId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!intervention) return;
    // Default to the day AFTER the source (most common case: "replay tomorrow")
    const src = (intervention.scheduled_date || "").slice(0, 10);
    if (src) {
      const d = new Date(src + "T00:00:00");
      d.setDate(d.getDate() + 1);
      setDay(d.toISOString().slice(0, 10));
    }
    setTechId(intervention.technician_id || "");
  }, [intervention]);

  if (!intervention) return null;

  const activeTeam = (team || []).filter((u) => u.is_active !== false && u.role !== "external");
  const sourceDay = (intervention.scheduled_date || "").slice(0, 10);
  const sourceTime = (intervention.scheduled_date || "").slice(11, 16);
  const sourceTech = activeTeam.find((u) => u.id === intervention.technician_id);

  const submit = async () => {
    if (!day) { toast.error("Choisis un jour cible"); return; }
    if (!techId) { toast.error("Choisis un équipier"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/interventions/${intervention.id}/duplicate`, {
        start_day: day,
        technician_id: techId,
      });
      const targetName = activeTeam.find((u) => u.id === techId)?.name || "l'équipier";
      const dayFr = new Date(day + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
      toast.success(`Dupliquée pour ${targetName} le ${dayFr}`);
      onDuplicated?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md" data-testid="duplicate-iv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy size={18} weight="duotone" className="text-[#7CB2FF]" />
            Dupliquer l&apos;intervention
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source recap */}
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Modèle source</div>
            <div className="font-medium text-white/90 truncate">{intervention.title || "Intervention"}</div>
            <div className="text-white/50 mt-1">
              {sourceTech?.name || "—"} · {sourceDay} · {sourceTime}
              {intervention.duration_min ? ` · ${intervention.duration_min} min` : ""}
            </div>
          </div>

          {/* Target tech */}
          <div>
            <Label className="text-xs flex items-center gap-1.5 mb-2">
              <User size={12} weight="duotone" /> Équipier cible
            </Label>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1" data-testid="duplicate-tech-grid">
              {activeTeam.map((u) => {
                const selected = u.id === techId;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setTechId(u.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs text-left transition-all ${
                      selected
                        ? "bg-[#005CFF]/20 border-[#005CFF] text-white shadow-lg shadow-[#005CFF]/20"
                        : "bg-white/[0.03] border-white/10 hover:border-white/25 text-white/70"
                    }`}
                    data-testid={`duplicate-tech-${u.id}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: u.color || "#005CFF" }}
                    />
                    <span className="truncate">{u.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target day */}
          <div>
            <Label htmlFor="duplicate-day" className="text-xs flex items-center gap-1.5 mb-2">
              <CalendarIcon size={12} weight="duotone" /> Jour cible
            </Label>
            <input
              id="duplicate-day"
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none focus:border-[#005CFF]"
              data-testid="duplicate-day-input"
            />
          </div>

          <Button
            onClick={submit}
            disabled={saving || !day || !techId}
            className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC] gap-2"
            data-testid="duplicate-submit-btn"
          >
            <Copy size={14} weight="bold" />
            {saving ? "Duplication…" : "Dupliquer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
